"use server"

import { sdk } from "@lib/config"
import { revalidateTag } from "next/cache"
import { headers } from "next/headers"
import { redirect } from "next/navigation"
import {
  getAuthHeaders,
  getCacheTag,
  getCartId,
  setAuthToken,
} from "./cookies"

type JwtPayload = {
  actor_id?: string
  auth_identity_id?: string
  user_metadata?: Record<string, unknown>
}

function decodeJwtPayload(token: string): JwtPayload {
  const parts = token.split(".")
  if (parts.length < 2) {
    throw new Error("Invalid authentication token")
  }

  const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/")
  const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4)
  const json = Buffer.from(padded, "base64").toString("utf8")
  return JSON.parse(json) as JwtPayload
}

async function getPublicBaseUrl(): Promise<string> {
  const h = await headers()
  const host = h.get("x-forwarded-host") || h.get("host")

  if (host) {
    const proto =
      h.get("x-forwarded-proto") ||
      (host.includes("localhost") ||
      host.startsWith("127.") ||
      host.startsWith("10.")
        ? "http"
        : "https")
    return `${proto}://${host}`
  }

  return process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:13033"
}

async function transferCartAfterAuth() {
  const cartId = await getCartId()

  if (!cartId) {
    return
  }

  const authHeaders = await getAuthHeaders()
  await sdk.store.cart.transferCart(cartId, {}, authHeaders)

  const cartCacheTag = await getCacheTag("carts")
  revalidateTag(cartCacheTag)
}

export async function startGoogleLogin(
  countryCode: string
): Promise<{ location?: string; loggedIn?: boolean; error?: string }> {
  if (
    !process.env.GOOGLE_CLIENT_ID &&
    process.env.NEXT_PUBLIC_GOOGLE_AUTH_ENABLED === "false"
  ) {
    return {
      error:
        "Google sign-in is not configured yet. Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to the backend, set NEXT_PUBLIC_GOOGLE_AUTH_ENABLED=true, then restart.",
    }
  }

  try {
    const baseUrl = await getPublicBaseUrl()
    const callback_url = `${baseUrl}/${countryCode}/auth/google/callback`

    const result = await sdk.auth.login("customer", "google", {
      callback_url,
    })

    if (typeof result === "object" && result && "location" in result) {
      return { location: String((result as { location: string }).location) }
    }

    if (typeof result === "string") {
      await setAuthToken(result)
      const customerCacheTag = await getCacheTag("customers")
      revalidateTag(customerCacheTag)
      await transferCartAfterAuth()
      return { loggedIn: true }
    }

    return { error: "Authentication failed" }
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

export async function completeGoogleAuth(
  countryCode: string,
  queryParams: Record<string, string>
): Promise<{ error?: string }> {
  let token: string

  try {
    const callbackToken = await sdk.auth.callback(
      "customer",
      "google",
      queryParams
    )

    if (typeof callbackToken !== "string" || !callbackToken) {
      return { error: "Authentication failed" }
    }

    token = callbackToken

    const decoded = decodeJwtPayload(token)
    const actorId = decoded.actor_id || ""

    if (!actorId) {
      const email = decoded.user_metadata?.email as string | undefined

      let linked = false
      try {
        await sdk.client.fetch("/auth/link", {
          method: "POST",
          headers: {
            authorization: `Bearer ${token}`,
          },
        })
        linked = true
      } catch {
        linked = false
      }

      if (!linked) {
        if (!email) {
          return { error: "Google account did not return an email address" }
        }

        await sdk.store.customer.create(
          { email },
          {},
          {
            authorization: `Bearer ${token}`,
          }
        )
      }

      const refreshResult = await sdk.client.fetch<{ token?: string }>(
        "/auth/token/refresh",
        {
          method: "POST",
          headers: {
            authorization: `Bearer ${token}`,
          },
        }
      )

      if (refreshResult?.token) {
        token = refreshResult.token
      } else {
        const refreshed = await sdk.auth.refresh()
        if (typeof refreshed === "string") {
          token = refreshed
        }
      }
    }

    await setAuthToken(token)

    const customerCacheTag = await getCacheTag("customers")
    revalidateTag(customerCacheTag)

    await transferCartAfterAuth()
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : String(error),
    }
  }

  redirect(`/${countryCode}/account`)
}
